import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../../Common/Interceptors/AxiosConfig";

const initialState = {
    errors: null,
    isLoading: false,
    // ✅ Cache bookings per persona and tab (e.g., 'client_requested', 'provider_completed')
    bookingsByTab: {},
    // ✅ Pagination meta per persona/tab
    tabMeta: {},
    // ✅ TTL cache per persona/tab page
    fetchedAtTabs: {}, 
    bookingDetailsData: [],
    // ✅ NEW: Persona-specific counts
    clientCounts: null,
    providerCounts: null,
    bookingCounts: null, // Legacy fallback
    // ✅ NEW: Notification tracking
    seenBookingIds: JSON.parse(localStorage.getItem('seenBookingIds') || '[]'),
};

const BookingSlice = createSlice({
    name: "booking",
    initialState,
    reducers: {
        setErrors: (state, action) => {
            // Ensure error is always a string or null to prevent React rendering crashes (Error #31)
            state.errors = typeof action.payload === 'string' 
                ? action.payload 
                : (action.payload?.message || action.payload?.errorMsg || "An unexpected error occurred");
        },
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setTabBookings: (state, action) => {
            const { tab, bookings = [], append = false, page = 1, limit = 20 } = action.payload || {};
            if (!tab) return;
            const pageKey = `${tab}_${page}_${limit}`;
            const existing = Array.isArray(state.bookingsByTab[tab]) ? state.bookingsByTab[tab] : [];
            state.bookingsByTab[tab] = append ? [...existing, ...bookings] : bookings;
            state.fetchedAtTabs[pageKey] = Date.now();
        },
        setTabMeta: (state, action) => {
            const { tab, meta, persona = 'client' } = action.payload || {};
            const key = `${persona}_${tab}`;
            if (!tab || !meta) return;
            state.tabMeta[key] = meta;
        },
        setBookingDetailsData: (state, action) => {
            state.bookingDetailsData = action.payload;
        },
        setBookingCounts: (state, action) => {
            const { clientCounts, providerCounts } = action.payload;
            state.clientCounts = clientCounts;
            state.providerCounts = providerCounts;
            // Sync legacy field for backward compatibility
            state.bookingCounts = clientCounts;
        },
        // ✅ NEW: Optimistically update a single booking (for instant UI feedback)
        updateBookingOptimistically: (state, action) => {
            const { bookingId, updates } = action.payload;
            let foundOldStatus = null;

            // Update in all cached tabs (booking may exist in 'all' and its status tab)
            Object.keys(state.bookingsByTab || {}).forEach((tabKey) => {
                const arr = state.bookingsByTab[tabKey];
                if (!Array.isArray(arr) || arr.length === 0) return;
                const idx = arr.findIndex(b => (b.id || b._id) === bookingId);
                if (idx === -1) return;

                // Capture old status once
                if (!foundOldStatus) {
                    const raw = arr[idx]?.bookingStatus;
                    foundOldStatus = raw === 'assigned' ? 'requested' : raw;
                }

                arr[idx] = { ...arr[idx], ...updates };
            });

            // Update counts once if status changed
            if (updates?.bookingStatus && state.bookingCounts && foundOldStatus) {
                const newRaw = updates.bookingStatus;
                const newStatus = newRaw === 'assigned' ? 'requested' : newRaw;
                const oldStatus = foundOldStatus;
                if (oldStatus === newStatus) return;

                if (oldStatus && state.bookingCounts[oldStatus] > 0) state.bookingCounts[oldStatus]--;
                if (newStatus && state.bookingCounts[newStatus] !== undefined) state.bookingCounts[newStatus]++;
            }
        },
        markBookingAsSeen: (state, action) => {
            const bookingId = action.payload;
            if (!state.seenBookingIds.includes(bookingId)) {
                state.seenBookingIds.push(bookingId);
                localStorage.setItem('seenBookingIds', JSON.stringify(state.seenBookingIds));
            }
        },
        markTabBookingsAsSeen: (state, action) => {
            const { persona, tabKey } = action.payload;
            const fullKey = `${persona}_${tabKey}`;
            const bookings = state.bookingsByTab[fullKey] || [];
            let updated = false;
            bookings.forEach(b => {
                const id = b.id || b._id;
                if (!state.seenBookingIds.includes(id)) {
                    state.seenBookingIds.push(id);
                    updated = true;
                }
            });
            if (updated) {
                localStorage.setItem('seenBookingIds', JSON.stringify(state.seenBookingIds));
            }
        }
    }
})

// ✅ Unified Hub Fetcher
// persona: 'client' | 'provider'
// tab: 'all'|'requested'|'accepted'|'paid'|'completed'|'cancelled'
export const getMyBookings = (tab = 'requested', page = 1, limit = 20, options = {}) => async (dispatch, getState) => {
    const { append = false, force = false, silent = false, persona = 'client' } = options;
    const state = getState().booking;
    const TTL_MS = 30 * 1000; 

    const stateKey = `${persona}_${tab}`;
    const pageKey = `${stateKey}_${page}_${limit}`;
    
    const lastFetched = state?.fetchedAtTabs?.[pageKey];
    if (!force && lastFetched && Date.now() - lastFetched < TTL_MS) {
        return;
    }

    if (!silent && state.isLoading) return;
    if (!silent) dispatch(setLoading(true));
    dispatch(setErrors(null));

    try {
        // Construct the Unified Hub URL with persona filtering
        let URL = `bookings/hub/unified?persona=${persona}&page=${page}&limit=${limit}`;
        if (tab && tab !== 'all') {
            URL += `&status=${tab}`;
        }

        const response = await axiosInstance.get(URL);
        const { success, data, message } = response?.data || {};

        if (success) {
            // Extract the correct side of the hub data
            const hubData = persona === 'client' ? data.clientSide : data.providerSide;
            
            if (hubData) {
                const { items = [], total = 0, page: resPage, limit: resLimit } = hubData;
                
                dispatch(setTabBookings({ 
                    tab: stateKey, 
                    bookings: items, 
                    append, 
                    page: resPage, 
                    limit: resLimit 
                }));
                
                dispatch(setTabMeta({ 
                    tab, 
                    persona,
                    meta: { total, page: resPage, limit: resLimit, pages: Math.ceil(total / resLimit) } 
                }));

                // Update counts for BOTH personas if returned, or just this one
                dispatch(setBookingCounts({
                    clientCounts: data.clientSide?.counts || state.clientCounts,
                    providerCounts: data.providerSide?.counts || state.providerCounts
                }));
            }
        } else if (!silent) {
            dispatch(setErrors({ errorMsg: message || 'Failed to fetch hub data' }));
        }
    } catch (error) {
        const errorMsg = error?.response?.data?.message || error.message || "something went wrong";
        if (!silent) dispatch(setErrors({ errorMsg }));
    } finally {
        if (!silent) dispatch(setLoading(false));
    }
};

// export const getMyBookings = () => async (dispatch) => {
//     dispatch(setLoading(true));
//     try {
//         let URL = "test-cashfree-public-key";
//         const response = await axiosInstance.get(URL);
//         
//         const { status, message, data } = response?.data || {};
//         if (status === "success") {
//             // dispatch(setMyBookingData(data))
//         }
//         dispatch(setLoading(false));
//     } catch (error) {
//         const errorMsg = error?.response?.data?.message || error.message || "something went wrong"
//         dispatch(setLoading(false)); // ✅ Fixed: was missing dispatch
//         dispatch(setErrors({ errorMsg }))
//     }
// }


// ✅ Renamed to match your import
export const BookingDetails = (bookingId) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        let URL = `bookings/${bookingId}`;
        const response = await axiosInstance.get(URL);
        const { success, data } = response?.data || {};
        if (success) {
            dispatch(setBookingDetailsData(data))
        }
        dispatch(setLoading(false));
    } catch (error) {
        const errorMsg = error?.response?.data?.message || error.message || "something went wrong"
        dispatch(setLoading(false)); // ✅ Fixed: was missing dispatch
        dispatch(setErrors({ errorMsg }))
    }
}

export const updateBooking = (bookingId, bookingStatus, options = {}) => async (dispatch) => {
    const { persona = 'client', additionalData = {} } = options;
    
    // ✅ OPTIMISTIC UPDATE: Update UI immediately for better UX
    dispatch(updateBookingOptimistically({
        bookingId,
        updates: {
            bookingStatus,
            ...additionalData
        }
    }));

    dispatch(setLoading(true));
    try {
        let URL = `bookings/${bookingId}/status`;
        const body = { status: bookingStatus, metadata: additionalData };
        const response = await axiosInstance.patch(URL, body);
        const { success, data } = response?.data || {};
        if (success) {
            dispatch(setBookingDetailsData(data));
            // ✅ Refetch page 1 for the relevant persona's tabs to sync
            await Promise.all([
                dispatch(getMyBookings('active', 1, 20, { persona, force: true, silent: true })),
                dispatch(getMyBookings('completed', 1, 20, { persona, force: true, silent: true })),
                dispatch(getMyBookings('cancelled', 1, 20, { persona, force: true, silent: true })),
            ]);
        } else {
            // ✅ Rollback optimistic update on error
            await dispatch(getMyBookings('all', 1, 20, { persona, force: true, silent: false }));
        }
        dispatch(setLoading(false));
    } catch (error) {
        const errorMsg = error?.response?.data?.message || error.message || "something went wrong";
        // ✅ Rollback optimistic update on error
        await dispatch(getMyBookings('all', 1, 20, { persona, force: true, silent: false }));
        dispatch(setLoading(false));
        dispatch(setErrors({ errorMsg }));
    }
}

export const { setErrors, setLoading, setTabBookings, setTabMeta, setBookingDetailsData, setBookingCounts, updateBookingOptimistically, markBookingAsSeen, markTabBookingsAsSeen } = BookingSlice.actions;

export default BookingSlice.reducer;
